import { useLocale } from "@hermes/intl";
import { useAuth } from "@hermes/shared";
import { captureException } from "@sentry/react";
import { type ReactNode, useEffect, useState } from "react";
import { Helmet } from "react-helmet-async";

import { useAppConfig } from "./config/context";

export type GTMEcommerceImpression = {
  name: string;
  id: number;
  price: number;
  brand: string;
  category: string;
  variant: string;
  list: string;
  position?: number;
};

export type GTMEcommerceLayer =
  | {
      ecommerce: {
        currencyCode: string;
        impressions: GTMEcommerceImpression[];
      };
      event: "gtm-ee-event";
      "gtm-ee-event-category": string;
      "gtm-ee-event-action": "Product Impressions";
      "gtm-ee-event-non-interaction": string;
      "gtm.uniqueEventId": number;
    }
  | {
      ecommerce: {
        currencyCode: string;
        detail: {
          actionField: {
            list: string;
          };
          products: {
            name: string;
            id: number;
            price: number;
            brand: string;
            category: string;
            variant: string;
          }[];
        };
      };
      event: "gtm-ee-event";
      "gtm-ee-event-category": string;
      "gtm-ee-event-action": "Product Details";
      "gtm-ee-event-non-interaction": string;
      "gtm.uniqueEventId": number;
    };

export type GTMDataLayer =
  | {
      event: string;
      [key: string]: any;
    }
  | {
      event: "virtualPageview";
      pageUrl: string;
      pageTitle: string;
      [key: string]: any;
    }
  | GTMEcommerceLayer;

declare global {
  interface Window {
    dataLayer: GTMDataLayer[];
    gtag?: (...args: any[]) => void;
    google_tag_data?: {
      ics: { getConsentState: (key: string, config: unknown) => number };
    };
    isRobot?: boolean;
    isPrerender?: boolean;
    [index: string]: any;
    VWO?: {
      flags?: string[];
      event?: (name: string) => void;
    };
    ym?: (...args: any[]) => void;
    _gtagSet?: boolean;
    _gtagInitialized?: boolean;
    _gtagError?: Event;
    _ymSet?: boolean;
    _ymInitialized?: boolean;
    _ymError?: Event;
    Cookiebot?: {
      consent: {
        marketing: boolean;
        preferences: boolean;
        statistics: boolean;
        necessary: boolean;
      };
    };
  }
}

function parseCookies(): Record<string, string> {
  return Object.fromEntries(
    document.cookie.split(";").map((pair) => {
      const cookie = pair.split("=");
      return [cookie[0]?.trim(), cookie[1]] as const;
    }),
  );
}

async function detectCookiesEnabled() {
  if (!navigator.cookieEnabled) return false;

  // Try CookieStore API
  if ("cookieStore" in window) {
    try {
      const cookies = await window["cookieStore"].getAll();
      if (cookies.length === 0) return false;
    } catch {
      return false;
    }
  }

  // Try test cookie
  document.cookie = "test_cookie=1; SameSite=Lax; path=/";
  if (!document.cookie.includes("test_cookie")) return false;

  return true;
}
(window as any).detectCookiesEnabled = detectCookiesEnabled;

function getDataWithTimeout(
  invoke: (callback: (value?: string) => void) => void,
  timeout = 1400,
): Promise<string | undefined> {
  return Promise.race([
    new Promise<string | undefined>((resolve) => {
      invoke((value) => {
        resolve(value?.toString());
      });
    }),
    new Promise<undefined>((_, reject) =>
      setTimeout(
        () => reject(new Error("Analytics cookie parsing timeout")),
        timeout,
      ),
    ),
  ]);
}

const tagId = `G-${import.meta.env.DOQ_GOOGLE_TAG_ID}`;

export async function parseGoogleSessionData() {
  try {
    const cookies = parseCookies();

    const _ga: string | undefined = cookies["_ga"];
    const _ga_session: string | undefined =
      cookies["_ga_" + import.meta.env.DOQ_GOOGLE_TAG_ID];
    let client_id: string | undefined = undefined;
    let session_id: string | undefined = undefined;
    let session_number: string | undefined = undefined;

    // Parse version 1 _ga client id
    if (_ga?.includes("GA1")) {
      // GA1.1.2002560640.1746517969
      // Skip first 6 symbols
      client_id = _ga.substring(6);
    }

    // Parse version 1 _ga session data
    if (_ga_session?.includes("GS1")) {
      // GS1.1.1746825440.14.0.17468254406.0.0.295082955
      const parts = _ga_session.split(".");

      session_id = parts[2];
      session_number = parts[3];
    }

    // Parse version 2 _ga session data
    if (_ga_session?.includes("GS2")) {
      // GS2.1.s1750233764$o2$g1$t1750235972$j10$l0$h0
      const segments = Object.fromEntries(
        _ga_session
          .substring(6)
          .split("$")
          .map((pair) => [pair[0], pair.slice(1)]),
      );
      session_id = segments["s"];
      session_number = segments["o"];
    }

    // Fallback in parallel
    await Promise.all([
      (async () => {
        if (!client_id && window.gtag) {
          client_id = await getDataWithTimeout((callback) => {
            window.gtag?.("get", tagId, "client_id", callback);
          });
        }
      })(),
      (async () => {
        if (!session_id && window.gtag) {
          session_id = await getDataWithTimeout((callback) => {
            window.gtag?.("get", tagId, "session_id", callback);
          });
        }
      })(),
      (async () => {
        if (!session_number && window.gtag) {
          session_number = await getDataWithTimeout((callback) => {
            window.gtag?.("get", tagId, "session_number", callback);
          });
        }
      })(),
    ]);

    // Without client id, parsing everything else is not required
    if (!client_id) {
      throw new Error("Couldn't parse ga cookies");
    }
    return {
      client_id,
      session_id,
      session_number,
    };
  } catch (error) {
    const cookies = parseCookies();
    captureException(error, {
      level: Object.keys(cookies).some((key) => key.startsWith("_ga"))
        ? "error"
        : "warning",
      tags: { analytics: "_ga-cookie-parsing" },
      extra: {
        error: {
          type: typeof error,
          message: error instanceof Error ? error.message : undefined,
          data: JSON.stringify(error),
        },
        data: cookies,
        isGtagDefined: Boolean(window.gtag),
        cookiesEnabled: await detectCookiesEnabled(),
        isRobot: window.isRobot,
        _gtagSet: window._gtagSet,
        _gtagInitialized: window._gtagInitialized,
        _gtagError: window._gtagError,
        consent: window.Cookiebot?.consent,
      },
    });
  }
  return undefined;
}
(window as any).parseGoogleSessionData = parseGoogleSessionData;

export async function parseYandexSessionData() {
  try {
    const cookies = parseCookies();
    let client_id = cookies["_ym_uid"];

    if (!client_id) {
      client_id = localStorage.getItem("_ym_uid") ?? undefined;
    }

    // Fallback
    if (!client_id && window.ym) {
      client_id = await getDataWithTimeout((callback) => {
        window.ym?.(
          import.meta.env.DOQ_YANDEX_METRIKA_KEY,
          "getClientID",
          callback,
        );
      });
    }

    if (!client_id) {
      throw new Error("Couldn't parse ym cookies");
    }

    return {
      client_id,
    };
  } catch (error) {
    const cookies = parseCookies();
    captureException(error, {
      level: Object.keys(cookies).some((key) => key.startsWith("_ym"))
        ? "error"
        : "warning",
      tags: { analytics: "_ym-cookie-parsing" },
      extra: {
        error: {
          type: typeof error,
          message: error instanceof Error ? error.message : undefined,
          data: JSON.stringify(error),
        },
        data: cookies,
        isYmDefined: Boolean(window.ym),
        cookiesEnabled: await detectCookiesEnabled(),
        isRobot: window.isRobot,
        _ymSet: window._ymSet,
        _ymInitialized: window._ymInitialized,
        _ymError: window._ymError,
        consent: window.Cookiebot?.consent,
      },
    });
  }
  return undefined;
}
(window as any).parseYandexSessionData = parseYandexSessionData;

export const GTagWrapper = ({ children }: { children: ReactNode }) => {
  const [locale] = useLocale();
  const { user } = useAuth();
  const { isPrerender } = useAppConfig();

  useEffect(() => {
    window.gtag?.("set", "language", locale.baseName);
  }, [locale.baseName]);

  const enableDebug = import.meta.env.DOQ_ENVIRONMENT === "development";

  const config = JSON.stringify({
    send_page_view: false,
    cookie_domain: "auto",
    ...(enableDebug && {
      debug_mode: true,
    }),
  });

  return (
    <>
      {!isPrerender && !user && (
        <Helmet>
          {Boolean(import.meta.env.DOQ_GOOGLE_TAG_ID) && (
            <script>{`gtag('config', '${tagId}', ${config}); window._gtagInitialized = true;`}</script>
          )}
          {Boolean(import.meta.env.DOQ_YANDEX_METRIKA_KEY) && (
            <script>
              {`ym(${import.meta.env.DOQ_YANDEX_METRIKA_KEY}, "init", {
                  clickmap:true,
                  trackLinks:true,
                  accurateTrackBounce:true,
                  webvisor:true,
                  ecommerce:"dataLayer"
                });window._ymInitialized = true;`}
            </script>
          )}
        </Helmet>
      )}
      {children}
    </>
  );
};

type GTEvent =
  | {
      event: "page_view";
      page_location: string;
      page_title: string;
    }
  | {
      event: "search";
      search_term: string;
      search_count: number;
      items: any;
    }
  | {
      event:
        | "submit-appointment-form"
        | "submit-appointment-form-validated"
        | "submit-appointment-form-success";
    }
  | {
      event: "begin_checkout";
      currency: string;
      shipping: number;
      tax: number;
      value: number;
      user_data?: unknown;
    }
  | {
      event: "form_submit";
      user_data?: unknown;
    }
  | { event: "profile_floating_booking" }
  | {
      event: "reschedule_after_visit";
    }
  | {
      event: "reschedule_change_time";
    }
  | {
      event: "reschedule_after_visit_success";
    }
  | {
      event: "reschedule_change_time_success";
    };

type CookiebotConsent =
  | "necessary"
  | "preferences"
  | "statistics"
  | "marketing";

const last: Record<string, string> = {};
export const useSendGTagEvent = () => {
  return {
    setParameter: (field: string, data: unknown) => {
      window.gtag?.("set", field, data);
    },
    hasConsent: (key: CookiebotConsent) => {
      if (window.Cookiebot?.consent) {
        return window.Cookiebot.consent[key] === true;
      }
      return true;
    },
    getConsents: () => {
      if (window.Cookiebot?.consent) {
        const consent = window.Cookiebot.consent;

        return [
          consent.necessary && "necessary",
          consent.preferences && "preferences",
          consent.statistics && "statistics",
          consent.marketing && "marketing",
        ].truthy() as CookiebotConsent[];
      }
      return [
        "necessary",
        "preferences",
        "statistics",
        "marketing",
      ] as CookiebotConsent[];
    },
    sendGTagEvent: ({
      event,
      allowDuplicate = true,
      ...params
    }: GTEvent & {
      /**
       * Whether to allow for back-to-back duplicate events or not
       *
       * @default true
       */
      allowDuplicate?: boolean;
    }) => {
      const token = JSON.stringify(params);
      if (!allowDuplicate && last[event] === token) return;
      last[event] = token;
      window.gtag?.("event", event, params);
    },
  };
};

// Add more A/B test flags
const FLAGS_EVENT_NAME = "flags-update";
const availableFlags = [
  "_flags_static_map",
  "_flags_static_map_scaled",
  "_flags_static_map_modal",
] as const;

const parseFlags = () => availableFlags.filter((flag) => window[flag] === true);

export const useVWO = () => {
  const [flags, setFlags] = useState<string[]>(parseFlags());

  useEffect(() => {
    const handler = () => {
      setFlags(parseFlags());
    };

    window.addEventListener(FLAGS_EVENT_NAME, handler);

    return () => window.removeEventListener(FLAGS_EVENT_NAME, handler);
  }, []);

  return {
    hasVWOFlag(id: (typeof availableFlags)[number]) {
      if (import.meta.env.DOQ_ENVIRONMENT === "development") return true;

      return flags.includes(id);
    },
  };
};
